import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2 } from "lucide-react";
import { loadCompanyMasters, type CompanyMaster } from "@/lib/supplierMaster";
import { loadOrganizations, type Organization } from "@/lib/organizations";
import { useAuth } from "@/contexts/AuthContext";

interface MasterSelectorProps {
  open: boolean;
  onSelect: (company: CompanyMaster) => void;
  onCancel: () => void;
}

const LAST_ORG_KEY = "admin.lastOrgId";

const MasterSelector = ({ open, onSelect, onCancel }: MasterSelectorProps) => {
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState<CompanyMaster[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      loadCompanyMasters(),
      isAdmin ? loadOrganizations() : Promise.resolve([] as Organization[]),
    ]).then(([cs, orgs]) => {
      setCompanies(cs);
      setOrganizations(orgs);
      if (isAdmin && orgs.length > 0) {
        const stored = localStorage.getItem(LAST_ORG_KEY);
        const valid = stored && orgs.some((o) => o.id === stored) ? stored : orgs[0].id;
        setSelectedOrgId(valid);
      }
      setLoading(false);
    });
  }, [open, isAdmin]);

  const showOrgFilter = isAdmin && organizations.length > 1;

  const visibleCompanies = useMemo(() => {
    if (!showOrgFilter) return companies;
    return companies.filter((c) => c.organization_id === selectedOrgId);
  }, [companies, showOrgFilter, selectedOrgId]);

  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId);
    localStorage.setItem(LAST_ORG_KEY, orgId);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            Elegir empresa
          </DialogTitle>
          <DialogDescription className="text-xs">
            {showOrgFilter
              ? "Selecciona primero la asesoría y después la empresa."
              : "selecciona la empresa que vas a tramitar"}
          </DialogDescription>
        </DialogHeader>

        {showOrgFilter && (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Asesoría
            </Label>
            <Select value={selectedOrgId} onValueChange={handleOrgChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona asesoría" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : visibleCompanies.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {showOrgFilter
              ? "No hay empresas en esta asesoría."
              : "No hay maestros disponibles."}
          </p>
        ) : (
          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2 min-h-0">
            {showOrgFilter && (
              <p className="mb-2 text-xs text-muted-foreground">
                Empresa
              </p>
            )}
            <div className="space-y-2">
              {visibleCompanies.map((c) => (
                <Button
                  key={c.id}
                  variant="outline"
                  className="w-full justify-start gap-3 text-sm font-medium h-11"
                  onClick={() => onSelect(c)}
                >
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  {c.name}
                </Button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MasterSelector;
